import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../../store';
import { getProjectDocumentSummary } from './sliceProjectSelectedDocuments';
import { IProjectViewData } from '../sliceProjectViews';
import { safeJsonParse } from '../../../util/DataUtils';
import { IFieldConfig } from './sliceProjectDocuments';
import { docTableActionsCallbacks } from '../../../components/General/DocTable/DocTableActions';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsOpenMultipleState = {
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    resetOpenMultipleDocumentsError: (state: Draft<IProjectDocumentsOpenMultipleState>) => {
        state.error = null;
    },
};

/**
 * Object of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that opens multiple documents at once.
     * @param config - Object that contains the project database's replicaId, document Ids and current view.
     * @returns Promise.
     */
    openMultipleDocuments: async (
        config: {
            replicaId: string;
            documentIds: string[];
            currentView: IProjectViewData;
        },
        thunkApi,
    ): Promise<void> => {
        const { replicaId, documentIds, currentView } = config;
        try {
            if (!currentView) {
                return Promise.reject(new Error('Failed to open multiple documents'));
            }
            const { viewStructure, viewId } = currentView;
            const fieldSchema = safeJsonParse<string[]>(currentView.fieldSchema, null);
            const fields = safeJsonParse<IFieldConfig[]>(currentView.fields, null);

            if (!fieldSchema || !fields) {
                return Promise.reject(new Error('Failed to open multiple documents'));
            }
            const selectedDocsSummary = await thunkApi.dispatch(
                getProjectDocumentSummary({
                    replicaId,
                    documentIds,
                    viewName: viewStructure,
                    columnName: viewId,
                    fields: ['Files', 'Subject', 'UniversalID'],
                }),
            );

            if (!selectedDocsSummary.payload.documents.length) {
                return Promise.reject(new Error('Failed to open multiple documents'));
            }
            selectedDocsSummary.payload.documents.forEach(document => {
                docTableActionsCallbacks.view({
                    replicaId,
                    universalId: document.UniversalID,
                    fileName: document.Files?.FileName ?? document.Subject,
                    fieldName: 'File',
                    fieldIndex: '0',
                });
            });
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsOpenMultipleState>) => {
        /**
         * Reducers for the 'openMultipleDocuments' thunk.
         */
        builder.addCase(
            openMultipleDocuments.pending,
            (state: Draft<IProjectDocumentsOpenMultipleState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            openMultipleDocuments.fulfilled,
            (state: Draft<IProjectDocumentsOpenMultipleState>) => {
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            openMultipleDocuments.rejected,
            (
                state: Draft<IProjectDocumentsOpenMultipleState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const openMultipleDocuments = createAsyncThunk(
    'project/documents/openMultiple',
    thunks.openMultipleDocuments,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectDocumentsOpenMultipleState = SliceProps;

/**
 * Create the redux slice.
 */
export const projectDocumentsOpenMultipleSlice = createSlice({
    name: 'projectDocumentsOpenMultiple',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { resetOpenMultipleDocumentsError } = projectDocumentsOpenMultipleSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsOpenMultipleSlice.reducer;
